import { BrowserCodeReader, BrowserMultiFormatReader } from '@zxing/browser';
import { NotFoundException } from '@zxing/library';
import { Button, Dropdown, Field, Link, Message, Spinner, TextInput } from 'Components/Core';
import { ListFieldSection } from 'Components/Layout';
import { actions as barcodeActions } from 'Data/ReduxViewModels/Barcode';
import { WorkflowType } from 'Data/ReduxViewModels/Barcode/types';
import { hasValue } from 'Lib/Any';
import { useLocalStorageState } from 'Lib/Hooks';
import deepEquals from 'Lib/Object/deepEquals';
import React, { useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonGroup } from 'semantic-ui-react';
import './Barcode.scss';

interface ISlideOutMenuProps {
  closeMenu: () => void;
  parentRef: any;
}

const Barcode: React.FC<React.PropsWithChildren<ISlideOutMenuProps>> = ({ closeMenu, parentRef }) => {
  const dispatch = useDispatch();
  const [codeReader] = useState(new BrowserMultiFormatReader());
  const [videoInputDevices, setVideoInputDevices] = useState<MediaDeviceInfo[]>([]);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useLocalStorageState(undefined, 'selectedVideoDevice');
  const workflowType = useSelector((state) => state.barcode.activeWorkflow?.WorkflowType);
  const resourceId = useSelector((state) => state.barcode.activeWorkflow?.ResourceId);
  const message = useSelector((state) => state.barcode.responseMessage);
  const barcode = useSelector((state) => state.barcode.barcode);
  const isSyncing = useSelector((state) => state.barcode.isSyncing);

  useEffect(() => {
    if (isSyncing || !hasValue(workflowType) || workflowType === WorkflowType.Invalid) return;

    //Stop Camera and clear barcode state
    closeMenu();
  }, [isSyncing, dispatch, closeMenu, workflowType]);

  useEffect(() => {
    if (videoInputDevices.length === 0) {
      BrowserCodeReader.listVideoInputDevices()
        .then((inputDevices) => {
          if (inputDevices[0]?.label === 'Video device 1') setVideoInputDevices([]);
          else {
            if (!deepEquals(videoInputDevices, inputDevices)) setVideoInputDevices(inputDevices);
            if (!selectedDevice) setSelectedDevice(inputDevices[0].deviceId);
          }
        })
        .catch(() => {});
      setIsVideoLoading(false);
    }
  }, [setSelectedDevice, selectedDevice, setIsVideoLoading, setVideoInputDevices, videoInputDevices]);

  useEffect(() => {
    if (videoInputDevices[0]?.label === 'Video device 1') return;
    BrowserMultiFormatReader.releaseAllStreams();

    codeReader
      .decodeFromVideoDevice(selectedDevice, hasValue(selectedDevice) ? 'video' : '', (resultVideo, err) => {
        if (resultVideo) {
          dispatch(barcodeActions.updateBarcode({ barcode: resultVideo.getText() }));
          dispatch(barcodeActions.postBarcode());
        }
        if (err && !(err instanceof NotFoundException)) {
        }
      })
      .catch(() => {});
    setIsVideoLoading(false);
  }, [videoInputDevices, isVideoLoading, selectedDevice, codeReader, dispatch]);

  useImperativeHandle(parentRef, () => ({
    close() {
      BrowserMultiFormatReader.releaseAllStreams();
      dispatch(barcodeActions.barcodeClear());
      dispatch(barcodeActions.setBarcodePanelOpen(false));
    },
  }));

  useEffect(() => {
    dispatch(barcodeActions.setBarcodePanelOpen(true));
  }, [dispatch]);

  return (
    <>
      <h1 className="Barcode-Heading">Scan Barcode or QR Code</h1>
      <div className="Barcode-Fields">
        <ListFieldSection wide>
          <Field
            input={<Dropdown />}
            isClearable={false}
            disabled={videoInputDevices.length === 0}
            options={videoInputDevices.map((i) => ({ value: i.deviceId, label: i.label }))}
            onChange={(e: any) => {
              setSelectedDevice(e.data.value?.value);
              setIsVideoLoading(true);
            }}
            value={selectedDevice}
            isSearchable={false}
            label="Camera/Video Input"
          />
          {(videoInputDevices.length === 0 || videoInputDevices[0]?.label === 'Video device 1') && (
            <Message severity="warning" message="No video device available." border={false} />
          )}
          <div className="Barcode-Camera">
            {videoInputDevices.length !== 0 && videoInputDevices[0]?.label !== 'Video device 1' && !isVideoLoading && (
              <video className="Barcode-videoInsert" id="video" width={507}></video>
            )}
            {isVideoLoading && (
              <div style={{ display: 'block', position: 'relative', height: `80px`, width: 'auto' }}>
                <Spinner large />
              </div>
            )}
          </div>
          <Field
            input={<TextInput />}
            label="ID"
            value={barcode}
            onKeyDown={(event: any) => {
              if (event.key === 'Enter') {
                dispatch(barcodeActions.postBarcode());
              }
            }}
            onChange={(e: any) => dispatch(barcodeActions.updateBarcode({ barcode: e.data.value }))}
          />
          <Message severity={message?.MessageType} message={message?.Text} border={false} key={message?.Text} />
          <ListFieldSection>
            {message?.MessageType === 'warning' && (
              <Link to={`/equipment/${resourceId}`} onClick={closeMenu}>
                Remove Association on Equipment Page
              </Link>
            )}
          </ListFieldSection>
          <ButtonGroup className="Barcode-ButtonGroup">
            <Button
              primary
              onClick={() => dispatch(barcodeActions.postBarcode())}
              disabled={barcode?.length === 0}
              loading={isSyncing}
            >
              OK
            </Button>
            <Button onClick={closeMenu}>Cancel</Button>
          </ButtonGroup>
        </ListFieldSection>
      </div>
    </>
  );
};

export { Barcode };
export default Barcode;
